/**
 * Sales Delivery Challan / Gate Pass — printable HTML for a retail sale.
 * Distinct from `deliveryChallanHtml.js` which handles inter-branch transfers.
 * Matches the Amaravati Automotives A4 template (letterhead, sold-to block,
 * vehicle particulars, receiver + authorised signatures).
 */
function esc(v) {
  if (v == null) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function saleDeliveryChallanHtml(dc) {
  const s = dc.sold_to || {};
  const v = dc.vehicle || {};
  const b = dc.branch || {};
  const co = dc.dealership || {};
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Delivery Challan ${esc(dc.dc_no)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: "Times New Roman", Georgia, serif; color: #000; margin: 0; padding: 0; font-size: 13px; }
  .doc { border: 2px solid #000; padding: 14px 18px; }
  .header { display: flex; align-items: center; justify-content: space-between; }
  .brand { font-family: Georgia, "Times New Roman", serif; }
  .brand .big { font-size: 22px; font-weight: bold; color: #062a5b; letter-spacing: 0.5px; }
  .brand .sub { font-size: 13px; color: #062a5b; font-weight: 600; }
  .brand .tag { font-size: 11px; color: #062a5b; margin-top: 3px; letter-spacing: 3px; }
  .tvs { text-align: right; }
  .tvs .tvs-label { font-family: Arial, sans-serif; font-weight: 900; font-size: 26px; color: #062a5b; letter-spacing: -1px; }
  .tvs .tvs-tag { font-size: 10px; color: #062a5b; letter-spacing: 2px; margin-top: 2px; }
  .title { text-align: center; text-transform: uppercase; font-weight: bold; font-size: 15px; margin: 14px 0 6px; letter-spacing: 3px; text-decoration: underline; }
  .meta { display: flex; justify-content: space-between; margin: 6px 0 12px; font-size: 12px; }
  .meta .cell { border-bottom: 1px dotted #000; min-width: 130px; padding: 0 6px; font-weight: bold; }
  .block { margin: 10px 0; line-height: 1.7; }
  .row { display: flex; gap: 8px; align-items: flex-end; }
  .row .lbl { white-space: nowrap; }
  .fill { flex: 1; border-bottom: 1px dotted #000; padding: 0 6px; font-weight: 600; min-height: 20px; }
  .intro, .outro { text-align: center; font-style: italic; font-size: 12px; margin: 10px 0 6px; }
  .vehicle { margin: 8px 0; }
  .vehicle .row { margin-bottom: 8px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 30px; padding-top: 30px; }
  .sig-box { text-align: center; width: 40%; }
  .sig-line { border-top: 1px solid #000; padding-top: 4px; font-size: 12px; font-weight: 600; }
  .footer { display: flex; justify-content: space-between; margin-top: 22px; font-size: 11px; border-top: 1px solid #000; padding-top: 6px; }
  .footer .right { text-align: right; }
  .print-actions { padding: 8px; background: #eee; text-align: center; }
  .print-actions button { padding: 6px 14px; font-size: 13px; cursor: pointer; }
  @media print {
    .print-actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="print-actions">
    <button onclick="window.print()">Print</button>
    <button onclick="window.close()">Close</button>
  </div>
  <div class="doc">
    <div class="header">
      <div class="brand">
        <div class="big">AMARAVATI</div>
        <div class="sub">AUTOMOTIVES PVT LTD</div>
        <div class="tag">A U T H O R I S E D&nbsp;&nbsp;&nbsp;D E A L E R</div>
      </div>
      <div class="tvs">
        <div class="tvs-label">TVS</div>
        <div class="tvs-tag">M O T O R&nbsp;&nbsp;C O M P A N Y</div>
      </div>
    </div>

    <div class="title">Delivery Challan / Gate Pass</div>

    <div class="meta">
      <div><span>DC No: </span><span class="cell">${esc(dc.dc_no)}</span></div>
      <div><span>Date: </span><span class="cell">${esc(fmtDate(dc.date))}</span></div>
    </div>

    <div class="block">
      <div class="row"><span class="lbl">Sold to Mr / Mrs / M/s.:</span><span class="fill">${esc(s.name)}</span></div>
      <div class="row"><span class="lbl">${esc(s.guardian_relation || "S/O")}:</span><span class="fill">${esc(s.guardian_name)}</span></div>
      <div class="row"><span class="lbl">D.No / Street:</span><span class="fill">${esc(s.address)}</span></div>
      <div class="row" style="margin-top:6px">
        <span class="lbl">Pincode:</span><span class="fill" style="max-width:120px">${esc(s.pincode)}</span>
        <span class="lbl" style="margin-left:16px">Phone Number:</span><span class="fill">${esc(s.phone)}</span>
      </div>
    </div>

    <div class="intro">Delivered the following vehicle in good condition as per manufacturer's specifications.</div>

    <div class="vehicle">
      <div class="row">
        <span class="lbl">Chassis No:</span><span class="fill">${esc(v.chassis_no)}</span>
        <span class="lbl" style="margin-left:16px">Engine Number:</span><span class="fill">${esc(v.engine_no)}</span>
      </div>
      <div class="row">
        <span class="lbl">Model:</span><span class="fill">${esc(v.model)}${v.color ? " &mdash; " + esc(v.color) : ""}</span>
      </div>
      <div class="row">
        <span class="lbl">Invoice No:</span><span class="fill">${esc(dc.invoice_no)}</span>
      </div>
    </div>

    <div class="outro">Received the above vehicle in good condition with Toolkit, Manual, and all other accessories as per the bill without HB.</div>

    <div class="signatures">
      <div class="sig-box">
        <div class="sig-line">Receiver's Signature</div>
      </div>
      <div class="sig-box">
        <div class="sig-line">Authorised Signature</div>
      </div>
    </div>

    <div class="footer">
      <div>
        <div><b>GSTIN:</b> ${esc(co.gstin || "")}</div>
        <div><b>Branch:</b> ${esc(b.name || "")}</div>
      </div>
      <div class="right">
        <div><b>for ${esc(co.name || "AMARAVATI AUTOMOTIVES PVT LTD")}</b></div>
      </div>
    </div>
  </div>
  <script>window.onload = function() { setTimeout(function(){ window.print(); }, 250); };</script>
</body>
</html>`;
}
